import { useQuery } from '@tanstack/react-query';
import { useUser } from '@/stores';
import { useUIStore } from '@/stores/ui.store';
// Specific-file import (not the barrel) to keep this off any business-units →
// business-lines cycle. useSelectedUnit's own dependency chain never reaches
// back here.
import { useSelectedUnit } from '@/features/crm/business-units/hooks/useSelectedUnit';
import { crmKeys } from '../../shared/constants/queryKeys';
import { usePublicScoringConfig } from '../../settings/hooks/useSettings';
import { businessLinesApi } from '../services/business-lines.service';

export function useBusinessLines(enabled = true) {
  return useQuery({
    queryKey: crmKeys.businessLines,
    queryFn: () => businessLinesApi.list(true),
    staleTime: 1000 * 60,
    enabled,
  });
}

/**
 * Business-line selection (v1.4 FR-37, RR-6).
 *
 * - Flag-gated: reads `enableInventory` from the narrow public-config endpoint
 *   (the full settings endpoint is admin-only, BUG-CRM-19) — flag OFF means no
 *   line fetch at all, so flag-OFF tenants see zero behavior change (FR-29).
 * - Persistence: `{userId, lineId}` in the persisted UI store; a stored
 *   selection from a DIFFERENT user is discarded (RR-6 shared-machine rule).
 * - Fallback: stored selection when still valid, else the default line, else
 *   the first line.
 */
export function useSelectedBusinessLine() {
  const user = useUser();
  const { data: config } = usePublicScoringConfig();
  const inventoryEnabled = config?.enableInventory ?? false;
  const { data: lines = [], isLoading } = useBusinessLines(inventoryEnabled);
  const selection = useUIStore((s) => s.businessLineSelection);
  const setSelection = useUIStore((s) => s.setBusinessLineSelection);
  // The operating unit determines its line (a unit is bound to exactly one).
  // When the active unit pins a line, that line drives everything and the
  // standalone line switcher hides — picking a unit and a line separately was
  // redundant and let you build an inconsistent unit/line pair.
  const {
    selectedUnit,
    hasValidStoredPick,
    units: myUnits,
    isLoading: unitsLoading,
  } = useSelectedUnit();

  const unitLineId = selectedUnit?.businessLine?.id ?? null;

  // While my-units is in flight the unit's pin is UNKNOWN — treat the lines as
  // unresolved so the fallback chain yields no id yet (exactly the state
  // consumers already handle while the lines query itself loads). Otherwise
  // line-keyed queries fire for the stored/default line and refire when the
  // pin lands: interim wrong-line data plus refetch churn.
  const resolvedLines = unitsLoading ? [] : lines;
  const unitPinsLine = !!unitLineId && resolvedLines.some((l) => l.id === unitLineId);

  const stored = selection && user && selection.userId === user.id ? selection.lineId : null;
  const validStored = stored && resolvedLines.some((l) => l.id === stored) ? stored : null;
  // Unit-pinned line wins over any stored free choice. Falls back to the
  // stored → default → first chain only when no unit pins a line (e.g. the
  // tenant-wide default unit).
  const selectedLineId = unitPinsLine
    ? unitLineId
    : (validStored ?? resolvedLines.find((l) => l.isDefault)?.id ?? resolvedLines[0]?.id ?? null);

  // The standalone switcher earns its place in exactly one state: the caller is
  // ENGAGED in a workspace (an explicit remembered pick, or a single-unit user)
  // whose unit has NO bound line — the tenant-default-unit case. Everywhere
  // else it's redundant (unit pins the line) or meaningless (group-wide admin,
  // units still loading — the computed fallback unit is not an engagement).
  const inUnit = hasValidStoredPick || myUnits.length === 1;
  const showLineSwitcher =
    inventoryEnabled &&
    !unitsLoading &&
    lines.length > 1 &&
    inUnit &&
    !!selectedUnit &&
    !selectedUnit.businessLine;

  const selectLine = (lineId: string) => {
    if (user) {
      setSelection({ userId: user.id, lineId });
    }
  };

  return {
    inventoryEnabled,
    lines,
    isLoading,
    selectedLineId,
    selectLine,
    unitPinsLine,
    showLineSwitcher,
  };
}
